import React, { type ReactElement, type Ref } from 'react'
import { useKey } from 'react-use'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import { Input, type InputProps } from 'components/Input/Input'
import { Flex } from 'components/Layout'
import { useIsVisible } from 'hooks'
import { HINT_HEIGHT, SPACE } from 'Theme'
import { SuggestionsDropdown, type Suggestion } from './SuggestionsDropdown'

const GAP_BETWEEN_INPUT_AND_SUGGESTIONS = SPACE.PX_8

export interface InputWithSuggestionsProps<T = string>
  extends Omit<InputProps, 'data' | 'ref'> {
  data?: Suggestion<T>[]
  isLoading?: boolean
  onSelectFromSuggestions?: (option: Suggestion<T>) => void
}

export const InputWithSuggestions = React.forwardRef(
  (
    {
      data = [],
      isLoading,
      onChange,
      onSelectFromSuggestions,
      hasHint,
      ...props
    },
    ref
  ) => {
    const {
      ref: wrapperRef,
      isVisible: isMenuVisible,
      setIsVisible: setIsMenuVisible,
    } = useIsVisible({
      initialIsVisible: false,
      isOutsideClickDisabled: false,
    })

    useKey('Escape', () => {
      setIsMenuVisible(false)
    })

    return (
      <Flex
        flexDirection="column"
        data-test-id={DATA_TEST_ID.INPUT_WITH_SUGGESTIONS}
        ref={wrapperRef}
        position="relative"
      >
        <Input
          onChange={(e) => {
            if (e.target.value === '') {
              setIsMenuVisible(false)
            } else if (!isMenuVisible) {
              setIsMenuVisible(true)
            }
            onChange(e)
          }}
          ref={ref}
          hasHint={hasHint}
          {...props}
        />
        {isMenuVisible && (
          <SuggestionsDropdown
            data={data}
            isLoading={isLoading}
            onSelect={(value) => {
              onSelectFromSuggestions(value)
              setIsMenuVisible(false)
            }}
            top={`calc(100% + ${GAP_BETWEEN_INPUT_AND_SUGGESTIONS}${
              hasHint ? ` - ${SPACE.PX_5} - ${HINT_HEIGHT}` : ''
            })`}
          />
        )}
      </Flex>
    )
  }
) as <T>(
  props: InputWithSuggestionsProps<T> & { ref?: Ref<HTMLInputElement> }
) => ReactElement
